/**
 * Replenishment Hub — tabbed interface for replenishment features.
 * Uses URL-based tab routing for deep-linkable tabs.
 * Shared filter bar (Brand, Category, City, Store, Period) above tabs.
 *
 * Keep-alive rendering: ALL tabs stay mounted (hidden via display:none)
 * so that filter state and loaded data persist across tab switches.
 */
import React, { useMemo } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ReplenishmentProjectionTab from './ReplenishmentProjectionTab';
import OrderValidationTab from './OrderValidationTab';
import ReplenishmentSettingsTab from './ReplenishmentSettingsTab';
import OrderHistoryTab from './OrderHistoryTab';
import StockTransfer from '../Inventory/StockTransfer';
import TransferHistoryTab from '../Inventory/TransferHistoryTab';
import { ReplenishmentFilterProvider } from './ReplenishmentFilterContext';
import ReplenishmentFilterBar from './ReplenishmentFilterBar';

const tabs = [
  { label: 'Replenishment Projection', path: '/replenishment/projection', Component: ReplenishmentProjectionTab },
  { label: 'Stock Transfer', path: '/replenishment/stock-transfer', Component: StockTransfer },
  { label: 'Transfer History', path: '/replenishment/transfer-history', Component: TransferHistoryTab },
  { label: 'Order Validation', path: '/replenishment/order-validation', Component: OrderValidationTab },
  { label: 'Order History', path: '/replenishment/order-history', Component: OrderHistoryTab },
  { label: 'Settings', path: '/replenishment/settings', Component: ReplenishmentSettingsTab },
];

const ReplenishmentHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTabIndex = useMemo(() => {
    const idx = tabs.findIndex((t) => location.pathname === t.path);
    return idx >= 0 ? idx : 0;
  }, [location.pathname]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(tabs[newValue].path);
  };

  return (
    <ReplenishmentFilterProvider>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Fixed header: title + filter + tabs (do not scroll) */}
        <Box sx={{ flexShrink: 0 }}>
          {/* Page title */}
          <Box sx={{ mb: '20px' }}>
            <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
              Replenishment
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
              Purchase projections, order validation, and approval workflow
            </Typography>
          </Box>

          {/* Shared filter bar */}
          <ReplenishmentFilterBar />

          {/* Tab navigation */}
          <Box sx={{ borderBottom: '1px solid var(--color-border)', mb: 3 }}>
            <Tabs
              value={activeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab) => (
                <Tab key={tab.path} label={tab.label} />
              ))}
            </Tabs>
          </Box>
        </Box>

        {/* Scrollable tab content area — only this scrolls */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {/* Keep-alive tab content — all tabs stay mounted, inactive ones hidden */}
          {tabs.map((tab, idx) => {
            const TabComponent = tab.Component;
            return (
              <Box
                key={tab.path}
                sx={{ display: activeTabIndex === idx ? 'block' : 'none' }}
              >
                <TabComponent />
              </Box>
            );
          })}
        </Box>
      </Box>
    </ReplenishmentFilterProvider>
  );
};

export default ReplenishmentHub;
